import type React from 'react';

const createStyles = (styles: React.CSSProperties): React.CSSProperties => styles;

export const overlayStyles = createStyles({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	height: '100dvh',
	maxHeight: '100dvh',
	backgroundColor: 'rgba(0, 0, 0, 0.65)',
	backdropFilter: 'blur(10px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 1050,
	animation: 'modalFadeIn 0.25s ease-out',
	padding: '16px',
	paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
	overflowY: 'auto',
});

export const containerStyles = createStyles({
	backgroundColor: 'var(--color-bg-elevated)',
	borderRadius: 'var(--radius-xl, 12px)',
	boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
	width: '100%',
	maxWidth: '540px',
	maxHeight: 'calc(100dvh - 32px)',
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	animation: 'modalScaleIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
});

export const headerStyles = createStyles({
	padding: '32px 28px 0',
	textAlign: 'center',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '8px',
});

export const headerIconStyles = createStyles({
	width: '52px',
	height: '52px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '14px',
	color: '#ffffff',
	background: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	marginBottom: '4px',
	boxShadow: '0 4px 16px rgba(30, 86, 49, 0.3)',
});

export const titleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-display)',
	fontSize: 'var(--type-xl)',
	color: 'var(--color-text-primary)',
	letterSpacing: '-0.01em',
	lineHeight: 'var(--leading-tight)',
});

export const subtitleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--type-sm)',
	color: 'var(--color-text-secondary)',
	lineHeight: 'var(--leading-normal)',
});

export const sectionLabelStyles = createStyles({
	margin: '0 0 2px',
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-xs)',
	letterSpacing: 'var(--tracking-wide)',
	textTransform: 'uppercase',
	color: 'var(--color-text-tertiary)',
});

// ---- Informed-consent summary (disclosure rows) ----

export const summarySectionStyles = createStyles({
	display: 'flex',
	flexDirection: 'column',
	gap: '8px',
	padding: '20px 28px 4px',
});

export const summaryGroupStyles = createStyles({
	border: '1px solid var(--color-border-default)',
	borderRadius: 'var(--radius-lg, 8px)',
	backgroundColor: 'var(--color-bg-secondary)',
	overflow: 'hidden',
});

export const getSummaryHeaderStyles = (isOpen: boolean): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	width: '100%',
	minHeight: '48px',
	padding: '12px 14px',
	background: 'transparent',
	border: 'none',
	cursor: 'pointer',
	textAlign: 'left',
	color: 'var(--color-text-primary)',
	transition: 'background 180ms ease',
	backgroundColor: isOpen ? 'var(--color-bg-tertiary)' : 'transparent',
});

export const summaryIconTileStyles = createStyles({
	width: '32px',
	height: '32px',
	minWidth: '32px',
	borderRadius: '9px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#ffffff',
	background: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	flexShrink: 0,
});

export const summaryHeadingStyles = createStyles({
	flex: 1,
	minWidth: 0,
	margin: 0,
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-sm)',
	color: 'var(--color-text-primary)',
});

export const getChevronStyles = (isOpen: boolean): React.CSSProperties => ({
	color: 'var(--color-text-tertiary)',
	transition: 'transform 200ms ease',
	transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
	flexShrink: 0,
});

export const summaryBodyStyles = createStyles({
	margin: 0,
	padding: '0 14px 14px 58px',
	fontFamily: 'var(--font-family-regular)',
	fontSize: '13px',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-secondary)',
});

export const policyLinksRowStyles = createStyles({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '4px 14px',
	padding: '4px 28px 0',
});

export const policyLinkStyles = createStyles({
	fontFamily: 'var(--font-family-medium)',
	fontSize: '12px',
	color: 'var(--color-text-link)',
	textDecoration: 'underline',
	textUnderlineOffset: '2px',
});

// Button that reads as the old "Read the full …" text link but opens the
// in-app reader dialog instead of navigating.
export const policyLinkButtonStyles = createStyles({
	appearance: 'none',
	background: 'none',
	border: 'none',
	padding: '6px 0',
	margin: 0,
	fontFamily: 'var(--font-family-medium)',
	fontSize: '12px',
	color: 'var(--color-text-link)',
	textDecoration: 'underline',
	textUnderlineOffset: '2px',
	cursor: 'pointer',
	textAlign: 'left',
});

// ---- Legal-document reader dialog ----

export const getDocOverlayStyles = (isSm: boolean): React.CSSProperties => ({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	height: '100dvh',
	maxHeight: '100dvh',
	backgroundColor: 'rgba(0, 0, 0, 0.7)',
	backdropFilter: 'blur(12px)',
	display: 'flex',
	alignItems: isSm ? 'stretch' : 'center',
	justifyContent: 'center',
	// Above the consent modal (1050) so it stacks on top.
	zIndex: 1060,
	animation: 'modalFadeIn 0.2s ease-out',
	padding: isSm ? '0' : '20px',
	paddingBottom: isSm ? '0' : 'max(20px, env(safe-area-inset-bottom))',
	overflowY: 'auto',
});

export const getDocContainerStyles = (isSm: boolean): React.CSSProperties => ({
	backgroundColor: 'var(--color-bg-elevated)',
	borderRadius: isSm ? '0' : 'var(--radius-xl, 12px)',
	boxShadow: isSm ? 'none' : '0 25px 60px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.05)',
	width: '100%',
	maxWidth: isSm ? '100%' : '680px',
	height: isSm ? '100dvh' : 'auto',
	maxHeight: isSm ? '100dvh' : 'calc(100dvh - 40px)',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	animation: isSm ? 'modalFadeIn 0.2s ease-out' : 'modalScaleIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
});

export const docHeaderStyles = createStyles({
	display: 'flex',
	alignItems: 'flex-start',
	gap: '14px',
	padding: '20px 24px',
	borderBottom: '1px solid var(--color-border-subtle)',
	backgroundColor: 'var(--color-bg-elevated)',
	flexShrink: 0,
});

export const docHeaderTileStyles = createStyles({
	width: '44px',
	height: '44px',
	minWidth: '44px',
	borderRadius: '12px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#ffffff',
	background: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	boxShadow: '0 4px 14px rgba(30, 86, 49, 0.3)',
	flexShrink: 0,
});

export const docHeaderTextStyles = createStyles({
	flex: 1,
	minWidth: 0,
	display: 'flex',
	flexDirection: 'column',
	gap: '3px',
	paddingTop: '2px',
});

export const docEyebrowStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-semibold)',
	fontSize: '10.5px',
	letterSpacing: 'var(--tracking-wide)',
	textTransform: 'uppercase',
	color: 'var(--color-text-tertiary)',
});

export const docTitleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-display)',
	fontSize: 'var(--type-xl)',
	color: 'var(--color-text-primary)',
	letterSpacing: '-0.01em',
	lineHeight: 'var(--leading-tight)',
});

export const docCloseButtonStyles = createStyles({
	width: '40px',
	height: '40px',
	minWidth: '40px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: 'var(--radius-full, 9999px)',
	border: '1px solid var(--color-border-default)',
	background: 'var(--color-bg-secondary)',
	color: 'var(--color-text-secondary)',
	cursor: 'pointer',
	flexShrink: 0,
	transition: 'all 180ms ease',
});

export const docBodyStyles = createStyles({
	flex: 1,
	minHeight: 0,
	overflowY: 'auto',
	padding: '24px',
	paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
	backgroundColor: 'var(--color-bg-primary)',
});

export const docBodyInnerStyles = createStyles({
	maxWidth: '640px',
	margin: '0 auto',
});

export const docStateStyles = createStyles({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '12px',
	padding: '48px 24px',
	textAlign: 'center',
	minHeight: '220px',
	color: 'var(--color-text-secondary)',
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--type-sm)',
	lineHeight: 'var(--leading-normal)',
});

export const docRetryButtonStyles = createStyles({
	marginTop: '4px',
	padding: '10px 22px',
	minHeight: '44px',
	borderRadius: 'var(--radius-lg, 8px)',
	border: 'none',
	background: 'var(--color-brand-secondary)',
	color: '#ffffff',
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-sm)',
	cursor: 'pointer',
});

// ---- Consent option rows ----

export const consentSectionStyles = createStyles({
	display: 'flex',
	flexDirection: 'column',
	gap: '10px',
	padding: '16px 28px',
});

export const getItemStyles = (isChecked: boolean): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'flex-start',
	gap: '12px',
	padding: '14px 16px',
	borderRadius: 'var(--radius-lg, 8px)',
	border: `1.5px solid ${isChecked ? 'var(--color-success)' : 'var(--color-border-default)'}`,
	backgroundColor: isChecked ? 'rgba(34, 197, 94, 0.04)' : 'var(--color-bg-primary)',
	cursor: 'pointer',
	transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
	userSelect: 'none',
});

export const itemIconContainerStyles = (isChecked: boolean): React.CSSProperties => ({
	width: '40px',
	height: '40px',
	minWidth: '40px',
	borderRadius: '10px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#ffffff',
	background: isChecked
		? 'linear-gradient(135deg, var(--color-success) 0%, #16a34a 100%)'
		: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	transition: 'background 200ms ease',
	flexShrink: 0,
});

export const itemContentStyles = createStyles({
	flex: 1,
	minWidth: 0,
});

export const itemTitleRowStyles = createStyles({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
	gap: '8px',
	marginBottom: '3px',
});

export const itemTitleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-sm)',
	color: 'var(--color-text-primary)',
	lineHeight: 'var(--leading-tight)',
});

export const itemDescriptionStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: '12px',
	color: 'var(--color-text-tertiary)',
	lineHeight: 'var(--leading-normal)',
});

export const getPillStyles = (required: boolean): React.CSSProperties => ({
	display: 'inline-flex',
	alignItems: 'center',
	padding: '2px 8px',
	borderRadius: 'var(--radius-full, 9999px)',
	fontFamily: 'var(--font-family-semibold)',
	fontSize: '10px',
	letterSpacing: 'var(--tracking-wide)',
	textTransform: 'uppercase',
	color: required ? 'var(--color-brand-primary-dark)' : 'var(--color-text-tertiary)',
	backgroundColor: required ? 'rgba(30, 86, 49, 0.12)' : 'var(--color-bg-tertiary)',
	border: `1px solid ${required ? 'rgba(30, 86, 49, 0.25)' : 'var(--color-border-default)'}`,
	flexShrink: 0,
});

export const checkBoxWrapStyles = createStyles({
	display: 'flex',
	alignItems: 'center',
	minWidth: '28px',
	minHeight: '28px',
	flexShrink: 0,
});

// ---- Footer / CTA ----

export const footerStyles = createStyles({
	position: 'sticky',
	bottom: 0,
	display: 'flex',
	flexDirection: 'column',
	gap: '10px',
	padding: '12px 28px 28px',
	backgroundColor: 'var(--color-bg-elevated)',
	borderTop: '1px solid var(--color-border-subtle)',
});

export const microcopyStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: '11.5px',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-tertiary)',
	textAlign: 'center',
});

export const getButtonStyles = (enabled: boolean): React.CSSProperties => ({
	width: '100%',
	height: '50px',
	border: 'none',
	borderRadius: 'var(--radius-lg, 8px)',
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-sm)',
	letterSpacing: 'var(--tracking-wide)',
	cursor: enabled ? 'pointer' : 'not-allowed',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '10px',
	transition: 'all 250ms cubic-bezier(0.22, 1, 0.36, 1)',
	backgroundColor: enabled ? 'var(--color-brand-secondary)' : 'var(--color-bg-tertiary)',
	color: enabled ? '#ffffff' : 'var(--color-text-disabled)',
	boxShadow: enabled ? '0 4px 16px rgba(40, 100, 57, 0.35)' : 'none',
	transform: enabled ? 'translateY(0)' : 'none',
});
